import type { AppDispatch, RootState } from 'store';

/**
 * Why:
 * Non-React modules (bridges, axios helpers, utils) must not
 * `import { store } from 'store'` at module top-level. That creates a cycle:
 *   module → store → slice extras → axios → bridges → module
 * and can crash Storybook/app with "Cannot access … before initialization".
 *
 * When:
 * Call `bindStoreAccess` once after the Redux store exists (app boot in
 * index.tsx AppContext, Storybook preview if needed, or test beforeEach).
 * Not from random feature code.
 *
 * How:
 *   bindStoreAccess(dispatch, store.getState);
 *   getAppDispatch()?.(someAction());
 *   const platform = getAppState()?.setting?.platform;
 *
 * This file must stay a leaf: type-only imports from store, no axios/bridges/slices.
 */

type StoreAccess = {
  dispatch: AppDispatch;
  getState: () => RootState;
};

let access: StoreAccess | null = null;

export const bindStoreAccess = (
  dispatch: AppDispatch,
  getState: () => RootState
): void => {
  access = { dispatch, getState };
};

export const getAppDispatch = (): AppDispatch | null => access?.dispatch ?? null;

export const getAppState = (): RootState | null =>
  access ? access.getState() : null;

export const resetStoreAccessForTests = (): void => {
  access = null;
};
